import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MockHero } from "../components/MockHero";
import { createContact } from "../lib/api";
import { toast } from "sonner";

const Feature = ({ n, title, body }) => (
  <div className="flex flex-col gap-4">
    <div className="font-mono-num text-[13px]" style={{ color: "#7228C8" }}>
      {String(n).padStart(2, "0")}
    </div>
    <h3 className="text-[24px] font-bold tracking-hero leading-[1.15]" style={{ color: "#1E1924" }}>
      {title}
    </h3>
    <p className="text-[15px] leading-[1.6] max-w-prose-tight" style={{ color: "#70667A" }}>
      {body}
    </p>
  </div>
);

const Persona = ({ tag, title, body, label, dark }) => (
  <div
    className={`p-8 lg:p-10 rounded-2xl ${dark ? "" : ""}`}
    style={{
      background: dark ? "#1E1924" : "#fff",
      color: dark ? "#FAF9FB" : "#1E1924",
      border: dark ? "1px solid #1E1924" : "1px solid #DEDAE2",
    }}
  >
    <div
      className="wk-eyebrow"
      style={{ color: dark ? "rgba(250,249,251,0.55)" : "#70667A" }}
    >
      {tag}
    </div>
    <h3 className="mt-4 text-[28px] lg:text-[32px] font-bold tracking-hero leading-[1.1]">
      {title}
    </h3>
    <p
      className="mt-4 text-[15px] leading-[1.6] max-w-prose-tight"
      style={{ color: dark ? "rgba(250,249,251,0.7)" : "#70667A" }}
    >
      {body}
    </p>
    <div
      className="mt-8 inline-flex border px-3 py-2 text-[13px] font-medium"
      style={{
        borderColor: dark ? "rgba(250,249,251,0.35)" : "#DBD6E0",
        color: dark ? "#FAF9FB" : "#1E1924",
      }}
    >
      {label}
    </div>
  </div>
);

const emptyGuideForm = {
  nombre: "",
  apellidos: "",
  email: "",
  prefijo: "+34",
  prefijo_otro: "",
  whatsapp: "",
  canal: "email",
  consentimiento_guia: false,
  consentimiento_ofertas: false,
};

const GuideForm = () => {
  const [form, setForm] = useState(emptyGuideForm);
  const [busy, setBusy] = useState(false);

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.nombre.trim() || !form.apellidos.trim()) {
      toast.error("Completa nombre y apellidos.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Escribe un email válido.");
      return;
    }
    if (form.canal === "whatsapp" && !/^\d{6,15}$/.test(form.whatsapp)) {
      toast.error("Escribe un WhatsApp de 6 a 15 cifras.");
      return;
    }
    if (!form.consentimiento_guia) {
      toast.error("Necesitamos tu consentimiento para enviarte la guía.");
      return;
    }
    setBusy(true);
    try {
      const contact = await createContact({
        nombre: form.nombre,
        apellidos: form.apellidos,
        email: form.email,
        prefijo: form.prefijo === "otro" ? form.prefijo_otro : form.prefijo,
        whatsapp: form.whatsapp || null,
        canal: form.canal,
        consentimiento_guia: form.consentimiento_guia,
        consentimiento_ofertas: form.consentimiento_ofertas,
        origen: new URLSearchParams(window.location.search).get("utm_source") || null,
      });
      localStorage.setItem("wespik.guide.name", contact.nombre);
      window.location.assign("/gracias-guia");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se ha podido guardar el contacto.");
      setBusy(false);
    }
  };

  return (
    <form className="mt-10 grid gap-5" onSubmit={submit} noValidate data-testid="guide-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
          Nombre
          <input
            className="wk-input mt-1.5"
            value={form.nombre}
            maxLength={60}
            onChange={(event) => setField("nombre", event.target.value)}
            data-testid="guide-name-input"
          />
        </label>
        <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
          Apellidos
          <input
            className="wk-input mt-1.5"
            value={form.apellidos}
            maxLength={80}
            onChange={(event) => setField("apellidos", event.target.value)}
            data-testid="guide-lastname-input"
          />
        </label>
      </div>
      <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
        Email
        <input
          type="email"
          className="wk-input mt-1.5"
          value={form.email}
          maxLength={120}
          onChange={(event) => setField("email", event.target.value)}
          data-testid="guide-email-input"
        />
      </label>
      <fieldset className="border-0 p-0 m-0">
        <legend className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
          ¿Cómo lo quieres recibir?
        </legend>
        <div className="mt-2 flex gap-5">
          {["email", "whatsapp"].map((channel) => (
            <label key={channel} className="flex items-center gap-2 text-[14px]" style={{ color: "#1E1924" }}>
              <input
                type="radio"
                name="guide-channel"
                checked={form.canal === channel}
                onChange={() => setField("canal", channel)}
                data-testid={`guide-channel-${channel}`}
              />
              {channel === "email" ? "Email" : "WhatsApp"}
            </label>
          ))}
        </div>
      </fieldset>
      {form.canal === "whatsapp" ? (
        <div className="grid sm:grid-cols-[150px_1fr] gap-4">
          <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
            Prefijo
            <select
              className="wk-input mt-1.5"
              value={form.prefijo}
              onChange={(event) => setField("prefijo", event.target.value)}
              data-testid="guide-whatsapp-prefix-select"
            >
              {["+34", "+52", "+54", "+57", "+56", "+51", "+1", "otro"].map((prefix) => (
                <option key={prefix} value={prefix}>{prefix === "otro" ? "Otro" : prefix}</option>
              ))}
            </select>
          </label>
          <label className="text-[13px] font-medium" style={{ color: "#1E1924" }}>
            Número
            <input
              className="wk-input mt-1.5"
              inputMode="numeric"
              value={form.whatsapp}
              maxLength={15}
              onChange={(event) => setField("whatsapp", event.target.value.replace(/\D/g, ""))}
              data-testid="guide-whatsapp-number-input"
            />
          </label>
          {form.prefijo === "otro" ? (
            <label className="text-[13px] font-medium sm:col-span-2" style={{ color: "#1E1924" }}>
              Escribe el prefijo
              <input
                className="wk-input mt-1.5"
                placeholder="+44"
                value={form.prefijo_otro}
                maxLength={5}
                onChange={(event) => setField("prefijo_otro", event.target.value)}
                data-testid="guide-whatsapp-custom-prefix-input"
              />
            </label>
          ) : null}
        </div>
      ) : null}
      <label className="flex gap-3 text-[13px] leading-[1.5]" style={{ color: "#1E1924" }}>
        <input
          type="checkbox"
          checked={form.consentimiento_guia}
          onChange={(event) => setField("consentimiento_guia", event.target.checked)}
          data-testid="guide-consent-required-checkbox"
        />
        <span>Acepto que We·Spik use mis datos para enviarme la guía <Link className="underline" to="/privacidad" data-testid="guide-privacy-link">Privacidad</Link></span>
      </label>
      <label className="flex gap-3 text-[13px] leading-[1.5]" style={{ color: "#1E1924" }}>
        <input
          type="checkbox"
          checked={form.consentimiento_ofertas}
          onChange={(event) => setField("consentimiento_ofertas", event.target.checked)}
          data-testid="guide-offers-checkbox"
        />
        <span>Quiero recibir consejos y ofertas de We·Spik por el canal elegido.</span>
      </label>
      <button className="wk-btn wk-btn-primary justify-self-start" disabled={busy} data-testid="guide-submit-button">
        {busy ? "Guardando…" : "Quiero la guía"}
      </button>
    </form>
  );
};

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="wk-hero-bg">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-32">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-8" style={{ background: "rgba(114,40,200,0.08)", color: "#7228C8" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#7228C8" }} />
                <span className="text-[12px] font-medium tracking-wide uppercase">Pack de pronunciación</span>
              </div>
              <h1
                className="text-[44px] sm:text-[52px] lg:text-[68px] font-extrabold tracking-hero leading-[1.05]"
                style={{ color: "#1E1924" }}
                data-testid="landing-headline"
              >
                Deja de estudiar inglés.<br />
                <span style={{ color: "#7228C8" }}>Habla inglés.</span>
              </h1>
              <p className="mt-6 text-[17px] lg:text-[18px] leading-[1.6] max-w-prose-tight" style={{ color: "#70667A" }}>
                Dos sesiones de conversación con tu tutor elegido y un informe claro de
                pronunciación tras cada sesión. Sin cuotas, sin letras pequeñas.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/reservar" className="wk-btn wk-btn-primary" data-testid="hero-cta-primary">
                  Reservar mi primera sesión · $22
                </Link>
                <Link to="/tutores" className="wk-btn wk-btn-ghost" data-testid="hero-cta-secondary">
                  Ver tutores
                </Link>
              </div>
              <div className="mt-10 text-[13px]" style={{ color: "#70667A" }}>
                <span className="font-semibold" style={{ color: "#1E1924" }}>Tutores nativos británicos</span>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <MockHero />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT DOES · 3 blocks */}
      <section style={{ background: "#FAF9FB" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="wk-eyebrow mb-6">Qué hace</div>
          <h2 className="text-[36px] lg:text-[48px] font-bold tracking-hero leading-[1.08] max-w-[820px]" style={{ color: "#1E1924" }}>
            Tres cosas, hechas bien.<br />
            Lo demás no hace falta.
          </h2>
          <div className="mt-16 grid md:grid-cols-3 gap-10 lg:gap-12">
            <Feature
              n={1}
              title="Tu tutor, desde la primera sesión"
              body="Elige a tu tutor nativo y reserva el primer hueco. El pack incluye dos sesiones de conversación de 30 min."
            />
            <Feature
              n={2}
              title="Un informe tras cada sesión"
              body="Te decimos qué sonidos dices bien, cuáles fallan y qué practicar. Y en la segunda sesión ves si has mejorado."
            />
            <Feature
              n={3}
              title="Sin comisiones escondidas"
              body="El tutor cobra el 80%. Las plataformas clásicas se quedan entre el 30% y el 33%. Nosotros nos quedamos el 20% y lo decimos en la portada, no en la letra pequeña."
            />
          </div>
        </div>
      </section>

      {/* Journey — dark block */}
      <section className="wk-ink-block">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-20">
            <div>
              <div className="wk-eyebrow" style={{ color: "rgba(250,249,251,0.55)" }}>
                <style>{`.wk-ink-block .wk-eyebrow::before{background:rgba(250,249,251,0.4)}`}</style>
                El recorrido
              </div>
              <h2 className="mt-6 text-[36px] lg:text-[48px] font-bold tracking-hero leading-[1.08]">
                De reservar a hablar,<br />sin fricción.
              </h2>
              <p className="mt-6 text-[16px] leading-[1.6] max-w-prose-tight" style={{ color: "rgba(250,249,251,0.65)" }}>
                Un flujo de cuatro pasos. Sin planes interminables ni pagos por adelantado de dos meses.
              </p>
            </div>
            <div className="space-y-8">
              {[
                { id: "choose", t: "Eliges tutor y hueco", d: "Miras el catálogo, eliges el tutor que te encaja y reservas tu primera sesión." },
                { id: "book", t: "Reservas tu pack", d: "Dos sesiones de 30 min con tu tutor elegido y un informe tras cada conversación." },
                { id: "speak", t: "Hablas con tu tutor", d: "30 minutos de conversación real. Sin fichas ni monólogos." },
                { id: "report", t: "Terminas y recibes el diagnóstico fonético", d: "Un informe claro de los fonemas que ya controlas y los que conviene practicar." },
              ].map((s, i) => (
                <div key={s.id} className="flex gap-6 items-start">
                  <div className="font-mono-num text-[42px] font-bold tracking-hero leading-none opacity-40" style={{ color: "#FAF9FB" }}>
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="pt-2">
                    <h3 className="text-[19px] font-semibold tracking-tight">{s.t}</h3>
                    <p className="mt-2 text-[14px] leading-[1.6] max-w-prose-tight" style={{ color: "rgba(250,249,251,0.6)" }}>
                      {s.d}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* For who */}
      <section>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="wk-eyebrow mb-6">Para quién es</div>
          <h2 className="text-[36px] lg:text-[48px] font-bold tracking-hero leading-[1.08] max-w-[720px]" style={{ color: "#1E1924" }}>
            Dos lados. Cada uno gana lo que la vieja escuela le quita.
          </h2>
          <div className="mt-14 grid lg:grid-cols-2 gap-6">
            <Persona
              tag="Alumnos"
              title="Personas que estudian inglés desde hace años y siguen bloqueadas al hablar."
              body="Sin monólogos del profesor, sin fichas de deberes, sin pagar dos meses de golpe. Solo conversación real con nativos y una IA que te dice, con precisión, qué fonemas te delatan en cuanto abres la boca."
              label="Pruébalo por solo $22"
            />
            <Persona
              tag="Tutores nativos"
              title="Profesores hartos de que otras plataformas se lleven un tercio y les paguen con dos semanas de retraso."
              body="Cobras el 80% de cada clase y lo recibes en 1 semana en dólares, sin comisiones bancarias internacionales. Tú pones el ritmo. Nosotros ponemos el motor."
              label="80% para el tutor · 20% para We·Spik. Y lo pone en la portada."
              dark
            />
          </div>
        </div>
      </section>

      <section style={{ background: "#FAF9FB", borderTop: "1px solid #DEDAE2" }}>
        <div className="max-w-[880px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
          <div className="wk-eyebrow mb-5">Guía gratuita</div>
          <h2 className="text-[32px] lg:text-[44px] font-bold tracking-hero leading-[1.08]" style={{ color: "#1E1924" }}>
            Descarga gratis: Los 5 errores que frenan tu inglés profesional
          </h2>
          <GuideForm />
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ background: "#FAF9FB", borderTop: "1px solid #DEDAE2", borderBottom: "1px solid #DEDAE2" }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-14 lg:gap-24 items-center">
            <div>
              <h2 className="text-[40px] lg:text-[64px] font-extrabold tracking-hero leading-[1.05]" style={{ color: "#1E1924" }}>
                Deja de estudiar<br />
                inglés. <span style={{ color: "#7228C8" }}>Habla</span> inglés.
              </h2>
              <p className="mt-6 text-[17px] leading-[1.6] max-w-prose-tight" style={{ color: "#70667A" }}>
                Desde solo $22 con tu tutor elegido y diagnóstico de pronunciación.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/reservar" className="wk-btn wk-btn-primary text-center" data-testid="cta-final-primary">
                Reservar mi primera sesión · $22
              </Link>
              <Link to="/tutores" className="wk-btn wk-btn-ghost text-center" data-testid="cta-final-secondary">
                Antes ver el catálogo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
